/**
 * useTerminology — Spec 113: Dynamic-Specific Terminology
 * Spec 294 category 3: TanStack Query migration (2026-08-03).
 *
 * Provides customizable terminology labels for each hierarchy.
 * Reads from org settings (terminology JSONB column), merges with defaults,
 * and exposes a `t(key)` lookup function plus the full labels object.
 *
 * Usage:
 *   const { t, labels } = useTerminology();
 *   <span>{t('dominant')}</span>  // "Daddy", "Owner", "Master", etc.
 *
 * Migration notes (69 consumers — external signature is unchanged):
 * The pre-migration hook held a module-level `Map<orgId, terminology>` cache
 * plus two hand-rolled `window` event listeners standing in for TanStack's
 * own key/staleTime/invalidation machinery. Confirmed (grep, 2026-08-03)
 * the exact trigger set that must survive the migration unchanged:
 *   - `organizationChanged` — dispatched ONLY by OrganizationContext.tsx
 *     (org switch / org list load / logout). Old behavior: re-reads
 *     localStorage for the new org id and either serves that org's cached
 *     entry or fetches fresh — it does NOT clear other orgs' entries.
 *     New behavior: local `orgId` state is re-derived from localStorage on
 *     this event, which changes the query KEY -- TanStack serves a cache
 *     hit for a previously-visited org (same as the old Map) or fetches
 *     fresh for a new one.
 *   - `terminologyUpdated` — dispatched ONLY by `notifyTerminologyUpdated()`
 *     below, called from TerminologySection.tsx (org settings save) and
 *     TerminologyStep.tsx (registration wizard). Old behavior: deletes the
 *     CURRENT org's cache entry, forcing a re-fetch. New behavior:
 *     `queryClient.invalidateQueries` targeted at the current org's exact
 *     key -- same scope, not a bare/prefix invalidation of every org.
 * No other dispatcher of either event exists in the app (verified via
 * repo-wide grep) -- the trigger set is unchanged, not a superset or subset.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationAPI } from '../services/api';
import { unwrapResponse } from '../utils/unwrapResponse';
import { queryKeys } from './queries/queryKeys';

export interface TerminologyLabels {
  dominant: string;
  submissive: string;
  task: string;
  reward: string;
  consequence: string;
  protocol: string;
  ritual: string;
  dashboard: string;
  praise: string;
  economy: string;
}

export type TerminologyKey = keyof TerminologyLabels;

export const DEFAULT_TERMINOLOGY: TerminologyLabels = {
  dominant: 'Dominant',
  submissive: 'Submissive',
  task: 'Task',
  reward: 'Reward',
  consequence: 'Consequence',
  protocol: 'Protocol',
  ritual: 'Ritual',
  dashboard: 'Helm',
  praise: 'Praise',
  economy: 'Economy',
};

/**
 * Role-based default overrides.
 * When no org-level override exists for a key, the role default is used
 * instead of the global default. Merge order: org override > role default > global default.
 */
const ROLE_DEFAULTS: Partial<Record<string, Partial<TerminologyLabels>>> = {
  member: { economy: 'Earnings' },
  admin: { economy: 'Economy' },
  super_admin: { economy: 'Economy' },
};

export interface UseTerminologyReturn {
  /** Look up a single terminology label by key */
  t: (key: TerminologyKey) => string;
  /** Full merged labels object */
  labels: TerminologyLabels;
  /** Whether terminology is still loading from the API */
  loading: boolean;
  /** Force refresh from API */
  refresh: () => Promise<void>;
}

const ORG_STORAGE_KEY = 'selectedOrganization';
const ORG_CHANGED_EVENT = 'organizationChanged';
const TERMINOLOGY_UPDATED_EVENT = 'terminologyUpdated';

function getStoredOrgId(): string | null {
  try {
    const stored = localStorage.getItem(ORG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.id || null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function getStoredOrgRole(): string | null {
  try {
    const stored = localStorage.getItem(ORG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.role || null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Fetches an org's terminology overrides. Deliberately swallows errors and
 * resolves to `{}` (matching the pre-migration try/catch) rather than
 * throwing -- consumers never see an error state, only a silent fallback to
 * defaults, and the query itself never enters TanStack's error status.
 */
async function fetchTerminology(orgId: string): Promise<Partial<TerminologyLabels>> {
  try {
    const response = await organizationAPI.getSettings(orgId);
    return unwrapResponse<any>(response.data)?.settings?.terminology || {};
  } catch (error) {
    console.error('Failed to load terminology:', error); // intentional: falls back to default labels
    return {};
  }
}

export function useTerminology(): UseTerminologyReturn {
  const queryClient = useQueryClient();
  const [orgId, setOrgId] = useState<string | null>(() => getStoredOrgId());

  // Re-derive orgId from localStorage on org switch -- changes the query key.
  useEffect(() => {
    const handleOrgChanged = () => setOrgId(getStoredOrgId());
    window.addEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
    return () => window.removeEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
  }, []);

  // Invalidate the CURRENT org's entry when terminology is saved elsewhere.
  useEffect(() => {
    const handleTermUpdate = () => {
      const currentOrgId = getStoredOrgId();
      if (currentOrgId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.terminology.forOrg(currentOrgId) });
      }
    };
    window.addEventListener(TERMINOLOGY_UPDATED_EVENT, handleTermUpdate);
    return () => window.removeEventListener(TERMINOLOGY_UPDATED_EVENT, handleTermUpdate);
  }, [queryClient]);

  const query = useQuery({
    queryKey: queryKeys.terminology.forOrg(orgId),
    queryFn: () => fetchTerminology(orgId as string),
    enabled: orgId !== null,
    // Pre-migration cached indefinitely per org (a module-level Map, cleared
    // only by the terminologyUpdated event) -- staleTime: Infinity +
    // refetchOnWindowFocus: false reproduces that, so tab refocus doesn't
    // silently re-probe the settings endpoint the old code never re-hit.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Pre-migration loadTerminology() had no retry wrapper.
    retry: false,
  });

  const customTerms = query.data;

  // Merge custom terms with defaults, applying role-based defaults in between.
  // Merge order: org override > role default > global default.
  const labels = useMemo<TerminologyLabels>(() => {
    const role = getStoredOrgRole();
    const roleDefaults = role ? ROLE_DEFAULTS[role] ?? {} : {};
    return { ...DEFAULT_TERMINOLOGY, ...roleDefaults, ...(customTerms ?? {}) };
  }, [customTerms]);

  // Lookup function (memoized)
  const t = useCallback(
    (key: TerminologyKey): string => {
      return labels[key] || DEFAULT_TERMINOLOGY[key] || key;
    },
    [labels]
  );

  const refresh = useCallback(async () => {
    await query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return { t, labels, loading: query.isLoading, refresh };
}

/**
 * Dispatch this event after saving terminology to trigger all
 * useTerminology() instances to refresh.
 */
export function notifyTerminologyUpdated(): void {
  window.dispatchEvent(new Event(TERMINOLOGY_UPDATED_EVENT));
}
